/**
 * Sample Flow - State management, bottom sheet, cart integration
 *
 * Manages the two-pill variant picker, bottom sheet email capture,
 * Klaviyo subscription, Netlify function calls, and cart drawer integration.
 */
(function () {
  'use strict';

  /* ── Shared primitives ──
   * Cart, discount and Netlify logic lives in sample-core.js so the PDP flow
   * and the collection-page quick buy button behave identically. sample-core.js
   * must be loaded before this file.
   */
  var core = window.knpSampleCore;
  if (!core) {
    console.error('sample-flow.js: sample-core.js must load first');
    return;
  }

  var CONFIG = core.CONFIG;
  var withTimeout = core.withTimeout;
  var isValidEmail = core.isValidEmail;
  var getSectionsToRender = core.getSectionsToRender;
  var getSectionInnerHTML = core.getSectionInnerHTML;
  var renderCartSections = core.renderCartSections;
  var openCartDrawer = core.openCartDrawer;
  var addToCart = core.addToCart;
  var applyDiscountCode = core.applyDiscountCode;
  var isMember = core.isMember;
  var getMemberEmail = core.getMemberEmail;
  var setMember = core.setMember;
  var klaviyoSubscribe = core.klaviyoSubscribe;
  var netlifySubmit = core.netlifySubmit;
  var netlifyCheckCredit = core.netlifyCheckCredit;
  var netlifyClaim = core.netlifyClaim;
  var attachDiscountToCart = core.attachDiscountToCart;


  /* ── SampleFlow controller ── */

  function SampleFlow(container) {
    this.container = container;
    this.sectionId = container.dataset.section;
    this.data = JSON.parse(
      document.getElementById('SampleFlowData-' + this.sectionId).textContent
    );

    // State
    this.activePill = 'tin';     // 'tin' or 'sample'
    this.hasCredit = true;       // Free sample available
    this.sampleInCart = false;
    this.anySampleInCart = false;
    this.submitting = false;
    this.initialCheckDone = false;

    // DOM refs
    this.pillTin = container.querySelector('[data-pill="tin"]');
    this.pillSample = document.querySelector('#SampleButtonSlot [data-pill="sample"]') || container.querySelector('[data-pill="sample"]');
    this.badge = document.querySelector('#SampleButtonSlot [data-badge]') || container.querySelector('[data-badge]');
    this.sizeSelect = document.getElementById('SampleFlowSize-' + this.sectionId);
    this.sizeInput = document.getElementById('SampleFlowSizeInput-' + this.sectionId);
    this.sheetOverlay = document.getElementById('SampleSheet-' + this.sectionId);
    this.sheetForm = document.getElementById('SampleSheetForm-' + this.sectionId);
    this.sheetSuccess = document.getElementById('SampleSheetSuccess-' + this.sectionId);
    this.sheetPaid = document.getElementById('SampleSheetPaid-' + this.sectionId);
    this.emailInput = document.getElementById('SampleEmail-' + this.sectionId);
    this.emailError = document.getElementById('SampleEmailError-' + this.sectionId);
    this.toast = document.getElementById('SampleToast-' + this.sectionId);

    // Buy buttons (the block rendered after variant_picker)
    this.buyButtons = this.findBuyButtons();

    // Price container (theme renders this in the title block)
    this.priceContainer = document.getElementById('price-' + this.sectionId);

    // Shop Pay instalment line
    this.instalmentForm = document.getElementById('product-form-installment');

    // Hidden variant selects (theme's original picker)
    this.variantSelects = document.getElementById('variant-selects-' + this.sectionId);

    this.init();
  }

  SampleFlow.prototype.findBuyButtons = function () {
    // Walk siblings after our container to find the product-form element
    var el = this.container.nextElementSibling;
    while (el) {
      var pf = el.querySelector ? el.querySelector('product-form') : null;
      if (pf) return el;
      if (el.tagName === 'PRODUCT-FORM') return el;
      el = el.nextElementSibling;
    }
    // Fallback: look within the product info wrapper
    var wrapper = this.container.closest('.product-information--inner');
    if (wrapper) {
      return wrapper.querySelector('product-form:not([data-sticky])');
    }
    return null;
  };

  SampleFlow.prototype.init = function () {
    this.bindEvents();
    this.checkIdentity();
    this.updateBuyButtonsVisibility();
    this.syncTinVariant();
  };

  /**
   * Cart convergence lives in the global sample-reconcile.js (loaded on all
   * pages, the cart included, with its own event hooks and retry budget).
   * This method remains as the hand-off point for the claim and signup paths.
   */
  SampleFlow.prototype.reconcileFreeSample = function () {
    if (window.knpReconcile) window.knpReconcile(0);
  };

  SampleFlow.prototype.bindEvents = function () {
    var self = this;

    if (!this.pillTin || !this.pillSample) {
      console.error('[sample-flow] pill elements not found. tin:', this.pillTin, 'sample:', this.pillSample);
      return;
    }

    // Pill clicks with iOS touchend fallback for first-tap reliability
    var pillLastHandled = { tin: 0, sample: 0 };

    function bindPillTap(pillEl, handler, key) {
      pillEl.addEventListener('click', function () {
        if (Date.now() - pillLastHandled[key] < 500) return;
        pillLastHandled[key] = Date.now();
        handler();
      });
      pillEl.addEventListener('touchend', function (e) {
        if (Date.now() - pillLastHandled[key] < 500) return;
        var touch = e.changedTouches && e.changedTouches[0];
        if (!touch) return;
        var endTarget = document.elementFromPoint(touch.clientX, touch.clientY);
        if (!endTarget || !pillEl.contains(endTarget)) return;
        pillLastHandled[key] = Date.now();
        e.preventDefault();
        handler();
      }, { passive: false });
    }

    bindPillTap(this.pillTin, function () { self.switchPill('tin'); }, 'tin');
    bindPillTap(this.pillSample, function () { self.onSamplePillClick(); }, 'sample');

    // Keyboard: arrow keys between pills
    [this.pillTin, this.pillSample].forEach(function (pill) {
      pill.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          e.preventDefault();
          var target = pill === self.pillTin ? self.pillSample : self.pillTin;
          target.focus();
          target.click();
        }
      });
    });

    // Size dropdown
    if (this.sizeInput) {
      this.sizeInput.addEventListener('change', function () {
        self.onSizeChange(this.value);
      });
    }

    // Direct listeners on each [data-action] element inside the sheet.
    // Belt-and-braces for iOS Safari: delegation on the transformed overlay
    // drops the first tap after open. Direct listeners on the button element
    // itself are reliable. Touchend fallback catches the iOS case where
    // touchstart fires but no click follows (first-tap-after-paint bug).
    function handleSheetAction(actionEl) {
      var action = actionEl.dataset.action;
      switch (action) {
        case 'close-sheet':
          self.closeSheet();
          break;
        case 'submit-sample':
          self.onSubmitEmail();
          break;
        case 'skip-offer':
          self.onSkipOffer();
          break;
        case 'add-tin-too':
          self.onAddTinToo();
          break;
        case 'go-cart':
          self.closeSheet(true);
          openCartDrawer();
          break;
        case 'add-more-samples':
          self.openSampleTray();
          break;
      }
    }

    var actionEls = this.sheetOverlay.querySelectorAll('[data-action]');
    actionEls.forEach(function (el) {
      var lastHandled = 0;

      // Direct click (primary path for mouse, keyboard, and iOS once warmed up)
      el.addEventListener('click', function (e) {
        if (Date.now() - lastHandled < 500) return;
        // "add-more-samples" is handled by sample-tray.js via a document-level
        // click listener (sample-tray.js L123). The stopPropagation below would
        // block that listener from firing. Skip this handler entirely for that
        // action so the click bubbles to document. If iOS first-tap issues appear
        // on this button specifically, move openSampleMode into sample-flow.js
        // or expose it globally instead of re-adding stopPropagation here.
        if (el.dataset.action === 'add-more-samples') return;
        lastHandled = Date.now();
        e.stopPropagation();
        handleSheetAction(el);
      });

      // touchend fallback: fires on iOS even when click is swallowed.
      // Only acts if click hasn't already fired in the last 500ms.
      el.addEventListener('touchend', function (e) {
        if (Date.now() - lastHandled < 500) return;
        // Same skip as click handler above — let sample-tray.js handle this action.
        if (el.dataset.action === 'add-more-samples') return;
        var touch = e.changedTouches && e.changedTouches[0];
        if (!touch) return;
        var endTarget = document.elementFromPoint(touch.clientX, touch.clientY);
        if (!endTarget || !el.contains(endTarget)) return;

        lastHandled = Date.now();
        e.preventDefault();
        handleSheetAction(el);
      }, { passive: false });
    });

    // Keyboard: Escape closes sheet
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !self.sheetOverlay.hidden) {
        self.closeSheet();
      }
    });

    // Email input: clear error on input, validate on blur
    if (this.emailInput) {
      this.emailInput.addEventListener('input', function () {
        self.clearEmailError();
      });
      this.emailInput.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          self.onSubmitEmail();
        }
      });
    }

    // Listen for variant changes from the hidden theme picker
    document.addEventListener('product:variant-change', function (e) {
      if (e.detail && e.detail.sectionId === self.sectionId) {
        self.onThemeVariantChange(e.detail.variant);
      }
    });

    // Listen for cart updates to detect if sample is added/removed elsewhere
    document.addEventListener('cart:item-added', function () {
      self.checkSampleInCart();
    });

    // Listen for cart changes (item removal, quantity updates)
    document.addEventListener('cart:refresh', function () {
      self.checkSampleInCart();
    });

    // Listen for line item changes (remove, quantity change in cart drawer)
    document.addEventListener('line-item:change:end', function () {
      self.checkSampleInCart();
    });

    // Re-check when cart drawer closes (covers remove button clicks)
    var cartDrawer = document.getElementById('Cart-Drawer');
    if (cartDrawer) {
      var observer = new MutationObserver(function (mutations) {
        mutations.forEach(function (m) {
          if (m.attributeName === 'class' && !cartDrawer.classList.contains('active')) {
            self.checkSampleInCart();
          }
        });
      });
      observer.observe(cartDrawer, { attributes: true, attributeFilter: ['class'] });
    }

    // Re-check on page visibility change (covers tab switching after cart edits)
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) {
        self.checkSampleInCart();
      }
    });

    // iOS keyboard-dismissal fix for the submit-sample CTA.
    // When the email input is focused, the iOS keyboard is up. Tapping the CTA
    // while the keyboard is dismissing causes the tap to be absorbed by iOS's
    // gesture handler — click never fires. Fix: on pointerdown on the CTA,
    // blur the email input immediately so the keyboard starts dismissing
    // BEFORE the tap completes. By the time click fires, the viewport has
    // already started re-flowing and the button is in a stable position.
    var submitCta = this.sheetOverlay.querySelector('[data-action="submit-sample"]');
    if (submitCta) {
      var preBlur = function () {
        if (self.emailInput && document.activeElement === self.emailInput) {
          self.emailInput.blur();
        }
      };
      // pointerdown fires before click on all modern browsers, including iOS 13+
      submitCta.addEventListener('pointerdown', preBlur);
      // touchstart fallback for older iOS
      submitCta.addEventListener('touchstart', preBlur, { passive: true });
    }

    // Belt-and-braces: on iOS Safari versions where dvh doesn't shrink the sheet
    // with the keyboard, scroll the email input into view when it's focused.
    if (window.visualViewport && this.emailInput) {
      var scrollInputIntoView = function () {
        if (self.sheetOverlay.hidden) return;
        if (document.activeElement !== self.emailInput) return;
        setTimeout(function () {
          self.emailInput.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }, 100);
      };
      this.emailInput.addEventListener('focus', scrollInputIntoView);
      window.visualViewport.addEventListener('resize', scrollInputIntoView);
    }
  };

  /* ── Identity check ── */

  SampleFlow.prototype.checkIdentity = function () {
    var self = this;
    var memberFlag = isMember() ? 'true' : null;
    var memberEmail = getMemberEmail() || null;

    // Show badge optimistically right away so it paints with the button
    this.initialCheckDone = true;
    this.updateBadge();

    // Fire cart check early so data is warm before user clicks
    this.cartCheckPromise = this.checkSampleInCart();

    if (memberFlag === 'true' && memberEmail) {
      // Returning subscriber: refine credit in background (correct badge if needed)
      this.refreshCredit();
    }
  };

  /**
   * Ask Netlify how much credit the member has left, tracking failure so a
   * cold-start timeout can be retried from checkSampleInCart rather than
   * sticking for the life of the page. Fails open (credit assumed), matching
   * the original behaviour; the drawer price is the source of truth.
   */
  SampleFlow.prototype.refreshCredit = function () {
    var self = this;
    if (this._creditFetchInFlight) return this._creditFetchInFlight;

    this._creditFetchInFlight = withTimeout(
      netlifyCheckCredit(getMemberEmail(), self.data.sampleVariant.id), 3000
    )
      .then(function (result) {
        self.hasCredit = result.credits_remaining > 0;
        if (result.discount_code) self.discountCode = result.discount_code;
        self.creditCheckFailed = false;
      })
      .catch(function () {
        self.hasCredit = true;
        self.creditCheckFailed = true;
      })
      .finally(function () {
        self._creditFetchInFlight = null;
        self.updateBadge();
      });
    return this._creditFetchInFlight;
  };

  SampleFlow.prototype.checkSampleInCart = function () {
    var self = this;
    var sampleId = parseInt(this.data.sampleVariant.id, 10);

    // Piggy-back a credit retry when the init check timed out on a cold
    // Netlify start: every cart event funnels through here, so the badge
    // recovers on the next interaction instead of staying wrong all page
    if (isMember() && this.creditCheckFailed) {
      this.refreshCredit();
    }

    // no-store: on back/forward navigations the browser serves /cart.js from
    // HTTP cache, painting IN BASKET from a cart state that no longer exists
    return fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var wasInCart = self.sampleInCart;
        var hadAnySample = self.anySampleInCart;

        // Check if THIS product's sample is in cart
        self.sampleInCart = cart.items.some(function (item) {
          return item.variant_id === sampleId;
        });

        // Check if ANY sample variant is in cart (by price — samples are £2)
        self.anySampleInCart = cart.items.some(function (item) {
          var title = (item.variant_title || item.title || '').toLowerCase();
          return title.indexOf('sample') > -1 ||
            title.indexOf('100ml') > -1 ||
            title.indexOf('tester') > -1 ||
            title.indexOf('a6') > -1;
        });

        // Only update badge if state actually changed AND initial check is done
        if (self.initialCheckDone &&
            (wasInCart !== self.sampleInCart || hadAnySample !== self.anySampleInCart)) {
          self.updateBadge();
        }
      })
      .catch(function () {
        // Network error — don't change state
      });
  };

  /* ── Pill switching ── */

  SampleFlow.prototype.switchPill = function (pill) {
    if (this.activePill === pill) return;

    this.activePill = pill;
    this.container.dataset.activePill = pill;

    // Update pill active states
    this.pillTin.classList.toggle('sf-pill--active', pill === 'tin');
    this.pillSample.classList.toggle('sf-pill--active', pill === 'sample');
    this.pillTin.setAttribute('aria-checked', pill === 'tin' ? 'true' : 'false');
    this.pillSample.setAttribute('aria-checked', pill === 'sample' ? 'true' : 'false');

    this.updateBuyButtonsVisibility();
    this.updatePriceDisplay();

    // When switching to tin pill, sync the hidden variant picker to the first/selected tin
    if (pill === 'tin') {
      this.syncTinVariant();
    }
  };

  SampleFlow.prototype.updateBuyButtonsVisibility = function () {
    /* Buy buttons, size dropdown, and instalments always stay visible */
  };

  /* ── Price display ── */

  SampleFlow.prototype.updatePriceDisplay = function () {
    if (!this.priceContainer) return;

    if (this.activePill === 'sample') {
      // Save the original price HTML so we can restore it
      if (!this._savedPriceHtml) {
        this._savedPriceHtml = this.priceContainer.innerHTML;
      }
      var priceHtml;
      if (this.hasCredit && !this.sampleInCart) {
        priceHtml = '<s style="opacity:0.5;text-decoration:line-through">' +
          this.data.sampleVariant.priceFormatted +
          '</s> <span style="color:var(--sf-free-color);font-weight:600">FREE</span>';
      } else {
        priceHtml = this.data.sampleVariant.priceFormatted;
      }
      this.priceContainer.innerHTML = '<div class="price">' + priceHtml + '</div>';
      this.priceContainer.classList.remove('visibility-hidden');
    } else {
      // Tin pill: restore saved price HTML
      if (this._savedPriceHtml) {
        this.priceContainer.innerHTML = this._savedPriceHtml;
        this.priceContainer.classList.remove('visibility-hidden');
      }
      // Also sync variant picker to update price if size changes
      this.syncTinVariant();
    }
  };

  /* ── Badge ── */

  SampleFlow.prototype.updateBadge = function () {
    if (!this.badge) return;

    // Remove all badge modifier classes
    this.badge.classList.remove('sf-pill__badge--free', 'sf-pill__badge--paid', 'sf-pill__badge--in-cart');
    this.pillSample.classList.remove('sf-pill--in-cart');

    if (this.sampleInCart) {
      // THIS product's sample is in the cart
      this.badge.textContent = 'IN BASKET';
      this.badge.classList.add('sf-pill__badge--in-cart');
      this.pillSample.classList.add('sf-pill--in-cart');
    } else if (this.anySampleInCart) {
      // A different product's sample is using the free credit
      this.badge.textContent = this.data.sampleVariant.priceFormatted;
      this.badge.classList.add('sf-pill__badge--paid');
    } else if (this.hasCredit) {
      // No samples in cart, credit available
      this.badge.textContent = 'FREE';
      this.badge.classList.add('sf-pill__badge--free');
    } else {
      this.badge.textContent = this.data.sampleVariant.priceFormatted;
      this.badge.classList.add('sf-pill__badge--paid');
    }

    // Show badge once state is determined
    this.badge.style.visibility = '';
  };

  /* ── Sample pill click ── */

  SampleFlow.prototype.onSamplePillClick = function () {
    this.switchPill('sample');

    // Check member status strictly from localStorage
    var memberFlag = isMember() ? 'true' : null;
    var memberEmail = getMemberEmail() || null;
    var isReturningMember = memberFlag === 'true' && memberEmail;

    // New visitor: always open sheet immediately. This is the primary path.
    if (!isReturningMember) {
      this.openSheet();
      return;
    }

    // Returning subscriber: use cached cart state (pre-fetched in init).
    // Ensure the init cart check has resolved, then act. The pill shows its
    // loading state synchronously so the press is acknowledged at once.
    var self = this;
    this.setPillLoading(true);
    var ready = this.cartCheckPromise || Promise.resolve();
    ready.then(function () {
      if (self.sampleInCart) {
        self.setPillLoading(false);
        self.showToast('Sample is already in your basket');
        return;
      }

      if (self.hasCredit && !self.anySampleInCart) {
        self.addSampleToCart(true);
        return;
      }

      self.addPaidSample();
    });

    // Refresh cart in background for next interaction
    this.checkSampleInCart();
  };

  SampleFlow.prototype.addPaidSample = function () {
    if (this.submitting) return;
    var self = this;
    this.submitting = true;
    this.setPillLoading(true);

    addToCart(this.data.sampleVariant.id, true)
      .then(function () {
        self.sampleInCart = true;
        self.anySampleInCart = true;
        self.updateBadge();
        self.openSheet('paid');
        self.syncPaidSheetPrice();
      })
      .catch(function (err) {
        self.showToast('Something went wrong. Please try again.');
        console.error('Sample cart add failed:', err);
      })
      .finally(function () {
        self.submitting = false;
        self.setPillLoading(false);
      });
  };

  /* The paid sheet's product line is server-rendered as "<title> - £2", but
     a member's single free-sample code follows the product page they are on,
     so the line they just added may in fact be the free one (and an earlier
     sample the paid one). Read the real cart line and say what it costs,
     then look once more after the discount has had a moment to settle. */
  SampleFlow.prototype.syncPaidSheetPrice = function () {
    var self = this;
    var el = this.sheetPaid && this.sheetPaid.querySelector('.sf-sheet__paid-product');
    if (!el) return;
    var variantId = this.data.sampleVariant.id;
    var title = (this.data.productTitle || '').trim();

    function apply() {
      return fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var line = (cart.items || []).find(function (it) { return it.variant_id === variantId; });
          if (!line) return;
          var isFree = line.final_line_price === 0;
          var price = isFree ? 'FREE' : self.data.sampleVariant.priceFormatted;
          el.textContent = (title ? title + ' - ' : '') + price;
        })
        .catch(function () {});
    }

    apply();
    setTimeout(apply, 1500);
  };

  /* Loading state on the sample pill: immediate feedback while cart calls run */
  SampleFlow.prototype.setPillLoading = function (loading) {
    if (!this.pillSample) return;
    if (loading) {
      this.pillSample.classList.add('sf-pill--loading');
      this.pillSample.setAttribute('aria-busy', 'true');
    } else {
      this.pillSample.classList.remove('sf-pill--loading');
      this.pillSample.removeAttribute('aria-busy');
    }
  };

  /* ── Bottom sheet ── */

  SampleFlow.prototype.openSheet = function (state) {
    var self = this;

    // Show requested state, hide others
    this.sheetForm.setAttribute('hidden', '');
    this.sheetSuccess.setAttribute('hidden', '');
    if (this.sheetPaid) this.sheetPaid.setAttribute('hidden', '');

    if (state === 'paid') {
      if (this.sheetPaid) this.sheetPaid.removeAttribute('hidden');
    } else if (state === 'success') {
      this.sheetSuccess.removeAttribute('hidden');
    } else {
      this.sheetForm.removeAttribute('hidden');
    }
    this.clearEmailError();
    if (this.emailInput) this.emailInput.value = '';

    // Show overlay
    this.sheetOverlay.removeAttribute('hidden');
    this.sheetOverlay.removeAttribute('aria-hidden');

    // Store currently focused element for restoration
    this.previousFocus = document.activeElement;

    // Double rAF: ensures the browser has committed the `hidden` removal and
    // painted a frame before we kick off the transition. This gives iOS Safari
    // a clean paint frame to establish hit-targets for buttons inside the
    // transformed .sf-sheet, which fixes the "first tap does nothing" bug.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        self.sheetOverlay.classList.add('sf-sheet--open');
      });
    });

    // Prevent body scroll AFTER the transition has had time to begin painting.
    // Applying this synchronously with the overlay reveal cancels in-flight
    // touch-to-click conversions on iOS Safari.
    setTimeout(function () {
      document.body.style.overflow = 'hidden';
    }, 50);

    // Focus the email input after the transition completes (280ms > 250ms transition).
    // On iOS Safari, avoid auto-focusing a text input if the user tapped a button,
    // because triggering the on-screen keyboard mid-transition can cause tap events
    // on other buttons in the sheet to be swallowed. We skip autofocus on touch devices.
    var isTouchDevice = ('ontouchstart' in window) || navigator.maxTouchPoints > 0;
    if (!isTouchDevice) {
      setTimeout(function () {
        if (self.emailInput && !self.sheetForm.hidden) self.emailInput.focus();
      }, 280);
    }

    // Set up focus trap
    this.setupFocusTrap();
  };

  SampleFlow.prototype.closeSheet = function (keepPill) {
    var self = this;

    this.sheetOverlay.classList.remove('sf-sheet--open');

    setTimeout(function () {
      self.sheetOverlay.setAttribute('hidden', '');
      self.sheetOverlay.setAttribute('aria-hidden', 'true');
    }, 250);

    // Restore body scroll
    document.body.style.overflow = '';

    // Switch back to tin pill unless told to keep current pill (e.g. after successful submit)
    if (!keepPill) {
      this.switchPill('tin');
    }

    // Restore focus
    if (this.previousFocus) {
      this.previousFocus.focus();
    }

    // Remove focus trap
    this.removeFocusTrap();
  };

  SampleFlow.prototype.setupFocusTrap = function () {
    var self = this;
    this._focusTrapHandler = function (e) {
      if (e.key !== 'Tab') return;

      var sheet = self.sheetOverlay.querySelector('.sf-sheet');
      var focusable = sheet.querySelectorAll(
        'button:not([hidden]):not([disabled]), input:not([hidden]):not([disabled]), [tabindex]:not([tabindex="-1"])'
      );

      if (focusable.length === 0) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', this._focusTrapHandler);
  };

  SampleFlow.prototype.removeFocusTrap = function () {
    if (this._focusTrapHandler) {
      document.removeEventListener('keydown', this._focusTrapHandler);
      this._focusTrapHandler = null;
    }
  };

  SampleFlow.prototype.showSuccessState = function (isFree) {
    this.sheetForm.setAttribute('hidden', '');
    if (this.sheetPaid) this.sheetPaid.setAttribute('hidden', '');
    this.sheetSuccess.removeAttribute('hidden');

    var title = this.sheetSuccess.querySelector('[data-success-title]');
    var subtitle = this.sheetSuccess.querySelector('[data-success-subtitle]');

    if (isFree) {
      title.textContent = 'Free sample added';
      subtitle.textContent = 'Discount applies at checkout';
    } else {
      title.textContent = 'Sample added';
      subtitle.textContent = this.data.sampleVariant.priceFormatted + ' added to your basket';
    }

    // Populate colour swatches in the "Add more samples" button
    var swatchRow = this.sheetSuccess.querySelector('[data-success-swatches]');
    if (swatchRow) {
      swatchRow.innerHTML = '';
      var mapEl = document.getElementById('ColourHexMap');
      if (mapEl) {
        try {
          var hexMap = JSON.parse(mapEl.textContent);
          var handles = Object.keys(hexMap);
          // Pick 4 random colours
          for (var si = handles.length - 1; si > 0; si--) {
            var sj = Math.floor(Math.random() * (si + 1));
            var tmp = handles[si]; handles[si] = handles[sj]; handles[sj] = tmp;
          }
          for (var sk = 0; sk < Math.min(4, handles.length); sk++) {
            var dot = document.createElement('span');
            dot.className = 'sf-sheet__cta-swatch';
            dot.style.backgroundColor = '#' + (hexMap[handles[sk]] || 'ccc').replace('#', '');
            swatchRow.appendChild(dot);
          }
        } catch (e) {}
      }
    }
  };

  /* ── Email validation ── */

  SampleFlow.prototype.showEmailError = function () {
    if (!this.emailInput || !this.emailError) return;
    this.emailInput.classList.add('sf-sheet__input--error');
    this.emailError.removeAttribute('hidden');
    this.emailInput.setAttribute('aria-describedby', this.emailError.id);
    this.emailInput.setAttribute('aria-invalid', 'true');
  };

  SampleFlow.prototype.clearEmailError = function () {
    if (!this.emailInput || !this.emailError) return;
    this.emailInput.classList.remove('sf-sheet__input--error');
    this.emailError.setAttribute('hidden', '');
    this.emailInput.removeAttribute('aria-describedby');
    this.emailInput.removeAttribute('aria-invalid');
  };

  /* ── Submit email (free sample) ── */

  SampleFlow.prototype.onSubmitEmail = function () {
    if (this.submitting) return;

    var email = this.emailInput ? this.emailInput.value.trim() : '';

    if (!isValidEmail(email)) {
      this.showEmailError();
      return;
    }

    this.submitting = true;
    var self = this;
    var variantId = this.data.sampleVariant.id;

    // Show loading state on CTA
    var cta = this.sheetOverlay.querySelector('[data-action="submit-sample"]');
    var ctaText = cta.querySelector('.sf-sheet__cta-text');
    var ctaLoading = cta.querySelector('.sf-sheet__cta-loading');
    cta.disabled = true;
    ctaText.setAttribute('hidden', '');
    ctaLoading.removeAttribute('hidden');

    // Fire all three in parallel — don't wait for Netlify before showing success
    core.setPendingFree(variantId);
    var cartPromise = addToCart(variantId, true);
    klaviyoSubscribe(email);
    var netlifyPromise = withTimeout(
      netlifySubmit(email, variantId, self.data.productTitle), 8000
    ).catch(function (err) {
      console.error('Netlify function error:', err);
      return null;
    });

    // Show success as soon as cart add completes — don't wait for Netlify/discount
    cartPromise
      .then(function (addResponse) {
        setMember(email);
        self.sampleInCart = true;
        self.anySampleInCart = true;
        self.updateBadge();
        self.submitting = false;
        cta.disabled = false;
        ctaText.removeAttribute('hidden');
        ctaLoading.setAttribute('hidden', '');
        self.showSuccessState(true);

        // Once Netlify responds (or times out), hand over to the reconciler:
        // it claims, applies the cookie, re-writes the cart so the discount
        // attaches, then verifies and retries until the cart shows it free.
        netlifyPromise.then(function (result) {
          var code = result && result.discount_code;
          core.setStoredCode(code);
          return applyDiscountCode(code);
        }).then(function () {
          self.reconcileFreeSample();
        });
      })
      .catch(function (err) {
        self.showToast('Something went wrong. Please try again.');
        console.error('Sample cart add failed:', err);
        self.submitting = false;
        cta.disabled = false;
        ctaText.removeAttribute('hidden');
        ctaLoading.setAttribute('hidden', '');
      });
  };

  /* ── Open sample tray ── */
  // NOTE: This function is NOT the primary handler for "add-more-samples".
  // That's handled by sample-tray.js's document-level click listener (L123),
  // which calls its own openSampleMode() directly. This function exists as
  // a fallback but its colour-tray:open-sample-mode event has no listener.
  // The actual working path is: click bubbles to document → sample-tray.js
  // catches it → openSampleMode() runs. See the skip in bindEvents above.

  SampleFlow.prototype.openSampleTray = function () {
    this.closeSheet(true);

    // Fetch current cart to know which samples are already added
    var self = this;
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var cartSampleIds = [];
        cart.items.forEach(function (item) {
          var t = (item.variant_title || item.title || '').toLowerCase();
          if (t.indexOf('sample') > -1 || t.indexOf('100ml') > -1 || t.indexOf('tester') > -1 || t.indexOf('a6') > -1) {
            cartSampleIds.push(item.variant_id);
          }
        });

        // NOTE: Nothing currently listens for this event. The listener was
        // in colour-tray.liquid's inline script (removed). sample-tray.js
        // handles sample mode via its own openSampleMode() function instead.
        document.dispatchEvent(new CustomEvent('colour-tray:open-sample-mode', {
          detail: { cartSampleIds: cartSampleIds }
        }));
      })
      .catch(function () {
        document.dispatchEvent(new CustomEvent('colour-tray:open-sample-mode', {
          detail: { cartSampleIds: [] }
        }));
      });
  };

  /* ── Skip offer (paid sample) ── */

  SampleFlow.prototype.onSkipOffer = function () {
    this.addPaidSample();
  };

  /* ── Add sample to cart (direct, no sheet) ── */

  SampleFlow.prototype.addSampleToCart = function (isFree) {
    if (this.submitting) return;

    var self = this;
    var variantId = this.data.sampleVariant.id;
    this.submitting = true;
    this.setPillLoading(true);

    // The discount cookie must land BEFORE the cart add: Shopify only
    // attaches a code to the cart during a real cart write, and only when the
    // code applies to something in it. With the cookie first, the add's own
    // section render comes back already discounted, so the drawer shows FREE
    // with no extra round trips. The claim re-scope runs in the background as
    // a backstop; /check pre-scopes the rule at page view.
    if (isFree) core.setPendingFree(variantId);
    var claim = isFree ? netlifyClaim(getMemberEmail(), variantId) : Promise.resolve();
    var appliedCode = isFree ? (this.discountCode || core.getStoredCode()) : null;
    var setup = isFree ? applyDiscountCode(appliedCode) : Promise.resolve();

    // Give a warm claim the chance to finish re-scoping before the add, so
    // the drawer's first paint is already free instead of £2 healing a beat
    // later. A cold start loses the race and the heal below covers it.
    var grace = isFree
      ? Promise.race([claim, new Promise(function (r) { setTimeout(r, 900); })])
      : Promise.resolve();

    setup
      .then(function () { return grace; })
      .then(function () { return addToCart(variantId, true); })
      .then(function (addResponse) {
        self.sampleInCart = true;
        self.anySampleInCart = true;
        self.updateBadge();
        openCartDrawer();
        if (isFree) {
          // The claim response carries the member's own code (immune to
          // other claimants). Skip the round trips when the add already
          // rendered free under the right cookie; otherwise swap the
          // cookie if needed and one re-write attaches the discount.
          core.lastHeal = claim.then(function (result) {
            var code = result && result.discount_code;
            if (code) core.setStoredCode(code);
            var item = addResponse && (addResponse.items ? addResponse.items[0] : addResponse);
            var alreadyFree = item && item.final_line_price === 0;
            if (alreadyFree && (!code || code === appliedCode)) return;
            var swap = (code && code !== appliedCode) ? applyDiscountCode(code) : Promise.resolve();
            return swap.then(function () {
              return attachDiscountToCart(addResponse && addResponse.key);
            });
          });
        }
      })
      .catch(function (err) {
        self.showToast('Something went wrong. Please try again.');
        console.error('Sample cart add failed:', err);
      })
      .finally(function () {
        self.submitting = false;
        self.setPillLoading(false);
      });
  };

  /* ── Add a tin too (upsell) ── */

  SampleFlow.prototype.onAddTinToo = function () {
    this.closeSheet();

    // Close the cart drawer if it was opened by the sample add
    var cartDrawer = document.getElementById('Cart-Drawer');
    if (cartDrawer && cartDrawer.classList.contains('active')) {
      cartDrawer.classList.remove('active');
      document.body.classList.remove('open-cc');
      document.body.classList.remove('open-cart');
    }

    this.switchPill('tin');

    // If single tin, it's already selected
    // If multi-tin, focus the size dropdown
    var self = this;
    setTimeout(function () {
      if (self.sizeInput) {
        self.sizeInput.focus();
      }
    }, 400);
  };

  /* ── Size dropdown ── */

  SampleFlow.prototype.onSizeChange = function (variantId) {
    if (!variantId) return;

    var vid = parseInt(variantId, 10);
    var self = this;

    // Remove the "Select size" click interceptor if it exists
    var addBtn = document.getElementById('AddToCart');
    if (addBtn && addBtn._sfSelectHandler) {
      addBtn.removeEventListener('click', addBtn._sfSelectHandler, true);
      addBtn._sfSelectHandler = null;
    }

    // Find the tin variant data for price
    var tin = null;
    for (var i = 0; i < this.data.tinVariants.length; i++) {
      if (this.data.tinVariants[i].id === vid) {
        tin = this.data.tinVariants[i];
        break;
      }
    }

    // Restore ATC button immediately
    if (addBtn) {
      addBtn.disabled = false;
      addBtn.classList.remove('sold-out');
      var textEl = addBtn.querySelector('.single-add-to-cart-button--text');
      if (textEl) textEl.dataset.content = window.theme && window.theme.variantStrings ? window.theme.variantStrings.addToCart : 'Add to basket';
      var priceEl = addBtn.querySelector('[data-atc-price]');
      if (priceEl && tin) {
        priceEl.textContent = tin.priceFormatted;
        priceEl.style.display = '';
      }
    }

    // Find the variant in allVariants to get its option values
    var allVariants = this.getAllVariants();
    var target = null;
    for (var i = 0; i < allVariants.length; i++) {
      if (allVariants[i].id === vid) {
        target = allVariants[i];
        break;
      }
    }

    if (!target || !this.variantSelects) return;

    // Directly click the matching radio in the hidden picker — simulates real user interaction.
    // This triggers the full theme variant change flow (fetch, price update, form input, URL, media).
    var fieldsets = this.variantSelects.querySelectorAll('fieldset');
    fieldsets.forEach(function (fieldset, index) {
      var optionValue = target.options[index];
      if (!optionValue) return;

      var select = fieldset.querySelector('select');
      if (select) {
        select.value = optionValue;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        var inputs = fieldset.querySelectorAll('input[type="radio"]');
        for (var j = 0; j < inputs.length; j++) {
          if (inputs[j].value === optionValue) {
            inputs[j].click();
            break;
          }
        }
      }
    });
  };

  /* ── Variant sync with hidden picker ── */

  SampleFlow.prototype.syncTinVariant = function () {
    if (!this.data.tinVariants.length) return;

    // If multi-tin with size select and a value is chosen, sync to it
    if (this.sizeInput && this.sizeInput.value) {
      this.onSizeChange(this.sizeInput.value);
      return;
    }

    // If single tin, sync to it
    if (!this.data.hasMultipleTins && this.data.firstTinId) {
      this.onSizeChange(String(this.data.firstTinId));
      return;
    }

    // Multi-tin with no selection: show "Select size" but keep button styled normally
    var self = this;
    var addBtn = document.getElementById('AddToCart');
    if (addBtn) {
      var priceEl = addBtn.querySelector('[data-atc-price]');
      if (priceEl) priceEl.style.display = 'none';
      var textEl = addBtn.querySelector('.single-add-to-cart-button--text');
      if (textEl) textEl.dataset.content = 'Select size';
      addBtn.disabled = false;
      addBtn.classList.remove('sold-out');
      // Intercept click to open the size dropdown instead of submitting
      addBtn._sfSelectHandler = function (e) {
        if (!self.sizeInput || !self.sizeInput.value) {
          e.preventDefault();
          e.stopImmediatePropagation();
          self.sizeInput.focus();
          // Flash the dropdown border to draw attention
          var selectWrap = self.sizeSelect;
          if (selectWrap) {
            selectWrap.style.outline = '2px solid var(--color-accent)';
            setTimeout(function () { selectWrap.style.outline = ''; }, 1500);
          }
        }
      };
      addBtn.addEventListener('click', addBtn._sfSelectHandler, true);
    }
  };

  /**
   * Sync the hidden theme variant picker to a specific variant ID.
   * Finds the matching option values and sets them on the hidden fieldsets,
   * then triggers a change event so the theme handles price/media/URL updates.
   */
  SampleFlow.prototype.syncVariantById = function (variantId) {
    if (!this.variantSelects) return;

    // Find the variant in all product variants via the JSON data
    var allVariants = this.getAllVariants();
    var target = null;
    for (var i = 0; i < allVariants.length; i++) {
      if (allVariants[i].id === variantId) {
        target = allVariants[i];
        break;
      }
    }

    if (!target) return;

    // Set each option on the hidden fieldsets
    var fieldsets = this.variantSelects.querySelectorAll('fieldset');
    var lastInput = null;

    fieldsets.forEach(function (fieldset, index) {
      var optionValue = target.options[index];
      if (!optionValue) return;

      var select = fieldset.querySelector('select');
      if (select) {
        select.value = optionValue;
        lastInput = select;
      } else {
        var inputs = fieldset.querySelectorAll('input[type="radio"]');
        for (var j = 0; j < inputs.length; j++) {
          if (inputs[j].value === optionValue) {
            inputs[j].checked = true;
            lastInput = inputs[j];
            break;
          }
        }
      }
    });

    // Trigger change from the last input element so it bubbles up through
    // variant-selects exactly like a real user click would.
    if (lastInput) {
      lastInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  SampleFlow.prototype.getAllVariants = function () {
    if (this._allVariants) return this._allVariants;

    // Use allVariants from SampleFlowData (includes option arrays for each variant)
    if (this.data.allVariants && this.data.allVariants.length) {
      this._allVariants = this.data.allVariants;
      return this._allVariants;
    }

    // Fallback: try to get from a script tag in the product info wrapper
    var wrapper = this.container.closest('.product-information--inner');
    if (!wrapper) return [];

    var jsonScript = wrapper.querySelector('script[type="application/json"]:not([id])');
    if (jsonScript) {
      try {
        this._allVariants = JSON.parse(jsonScript.textContent);
        return this._allVariants;
      } catch (e) {}
    }

    return [];
  };

  /* ── Theme variant change listener ── */

  SampleFlow.prototype.onThemeVariantChange = function (variant) {
    // If the theme picked a sample variant, switch to sample pill
    if (variant && variant.id === this.data.sampleVariant.id) {
      if (this.activePill !== 'sample') {
        this.activePill = 'sample';
        this.container.dataset.activePill = 'sample';
        this.pillTin.classList.remove('sf-pill--active');
        this.pillSample.classList.add('sf-pill--active');
        this.pillTin.setAttribute('aria-checked', 'false');
        this.pillSample.setAttribute('aria-checked', 'true');
        this.updateBuyButtonsVisibility();
      }
    }
  };

  /* ── Toast ── */

  SampleFlow.prototype.showToast = function (message) {
    if (!this.toast) return;

    var toast = this.toast;
    toast.textContent = message;
    toast.removeAttribute('hidden');

    requestAnimationFrame(function () {
      toast.classList.add('sf-toast--visible');
    });

    setTimeout(function () {
      toast.classList.remove('sf-toast--visible');
      setTimeout(function () {
        toast.setAttribute('hidden', '');
      }, 300);
    }, 4000);
  };

  /* ── Initialise ── */

  function initAll() {
    var containers = document.querySelectorAll('.sf-container');
    containers.forEach(function (container) {
      if (container.dataset.sfInit) return;
      container.dataset.sfInit = 'true';
      try {
        new SampleFlow(container);
      } catch (e) {
        console.error('[sample-flow] init error:', e);
      }
    });
  }

  // Script is defer — DOM is already parsed, run immediately
  initAll();

  // Dynamically load sample-tray.js and CSS
  var stScript = document.querySelector('script[src*="sample-flow.js"]');
  if (stScript) {
    var base = stScript.src.split('?')[0].replace('sample-flow.js', '');
    var s = document.createElement('script');
    s.src = base + 'sample-tray.js';
    s.defer = true;
    document.body.appendChild(s);
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = base + 'sample-tray.css';
    document.head.appendChild(link);
  }
})();
